import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { canCreateHospitalRequest, isTechnicalAuditorRole } from "@/helpers/hospitalColumnPolicy";
import HomeView from "@/views/home/HomeView.vue";
import SignIn from "@/views/authentication/SignIn.vue";
import CustomSignIn from "@/views/authentication/CustomSignIn.vue";
import CreateAccountIndex from "@/views/authentication/CreateAccount/index.vue";
import DonateCase from "@/views/donate/donateCase.vue";
import DonateWaqf from "@/views/donate/donateWaqf.vue";
import DonateCampaign from "@/views/donate/donateCampaign.vue";
import ZakatOptions from "../views/zakat/ZakatOptions.vue";
import ZakatDonate from "../views/zakat/ZakatDonate.vue";
import ZakatCalculate from "../views/zakat/ZakatCalculate.vue";
import CasesList from "../views/cases-list/CasesList.vue";
import CharityCasesList from "../views/charity-cases-list/CharityCasesList.vue";
import CharityList from "../views/charity-list/CharityList.vue";
import WaqfList from "../views/waqf-list/WaqfList.vue";
import CampaignList from "../views/campaign-list/CampaignList.vue";
import Checkout from "../views/checkout/Checkout.vue";
import PeriodicDonationCheckout from "../views/checkout/PeriodicDonationCheckout.vue";
import ZakatCheckout from "../views/checkout/ZakatCheckout.vue";
import { useScrollToSelectedCase } from "@/helpers/useScrollToSelectedCase";
import { verifySehattyToken } from "@/helpers/helpers";
import {
  getLocalValue,
  removeLocalValue,
  setLocalValue,
} from "@/hooks/useLocalStorage";
import JwtUtilService from "@/services/jwt.util.service";
import { STORE_ENUM } from "@/store/store.enums";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "landing",
    redirect: JwtUtilService.getClientID()?.includes("major-donor") ? "/my-wallets" : "/home",
  },
  {
    path: "/home",
    name: "home",
    component: HomeView,
  },
  {
    path: "/sign-in",
    name: "sign-in",
    meta: { authPage: true },
    component:
      process.env.VUE_APP_AUTHENTICATION_MODE === "custom"
        ? CustomSignIn
        : SignIn,
  },
  {
    path: "/create-account/:step",
    name: "create-account",
    meta: { authPage: true },
    component: CreateAccountIndex,
  },
  {
    path: "/forget-password",
    name: "forget-password",
    meta: { authPage: true },
    component: () => import("../views/authentication/ForgetPassword.vue"),
  },
  {
    path: "/reset-password",
    name: "reset-password",
    meta: { requiresAuth: true },
    component: () => import("../views/authentication/ResetPassword.vue"),
  },
  {
    path: "/login/admin",
    name: "admin-login",
    meta: { authPage: true },
    component:
      process.env.VUE_APP_AUTHENTICATION_MODE === "custom"
        ? () => import("../views/authentication/CustomLogin.vue")
        : () => import("../views/authentication/AdminSignin.vue"),
  },
  {
    path: "/upload-signed-agreement",
    name: "upload-signed-agreement",
    meta: { requiresAuth: true },
    component: () =>
      import("../views/authentication/UploadSignedAgreement.vue"),
  },
  {
    path: "/agreement-approval",
    name: "agreement-approval",
    meta: { requiresAuth: true },
    component: () => import("../views/authentication/AgreementApproval.vue"),
  },
  {
    path: "/callback",
    name: "callback",
    component: () => import("../views/authentication/Callback.vue"),
  },
  {
    path: "/partnership-request",
    name: "partnership-request",
    component: () =>
      import("../views/partnership-request/PartnershipRequest.vue"),
  },
  {
    path: "/about-us",
    name: "about-us",
    component: () => import("../views/about-us/about-us.vue"),
  },
  {
    path: "/delete-account",
    name: "delete-account",
    component: () => import("../views/delete-account/DeleteAccount.vue"),
  },
  {
    path: "/mark-close",
    name: "mark-close",
    component: () => import("../views/mark-close/MarkClose.vue"),
  },
  {
    path: "/gift-card",
    name: "gift-card",
    meta: { requiresAuth: true, redirectAfterAuth: true },
    component: () => import("../views/gift-card/GiftCard.vue"),
  },
  {
    path: "/ambassador-link/:id/:case_id/",
    name: "ambassador-link",
    component: () => import("../views/ambassador/AmbassadorLink.vue"),
  },
  {
    path: "/donate-case/:case_id",
    name: "donate-case",
    component: DonateCase,
  },
  {
    path: "/donate-waqf/:waqf_id",
    name: "donate-waqf",
    component: DonateWaqf,
  },
  {
    path: "/donate-campaign/:campaign_id",
    name: "donate-campaign",
    component: DonateCampaign,
  },
  {
    path: "/donate-hef-waqf/:id",
    name: "donate-hef-waqf",
    component: () => import("../views/donate/DonateAwqaafProduct.vue"),
  },
  {
    path: "/mobile/payment-confirmation",
    name: "mobile-payment-confirmation",
    component: () => import("../views/mobile/PaymentConfirmation.vue"),
  },
  {
    path: "/mobile/checkout",
    name: "mobile-checkout",
    component: () => import("../views/mobile/Checkout.vue"),
  },
  {
    path: "/mobile/payment-success",
    name: "mobile-payment-success",
    component: () => import("../views/mobile/PaymentResult.vue"),
  },
  {
    path: "/mobile/payment-pending",
    name: "mobile-payment-pending",
    component: () => import("../views/mobile/PaymentResult.vue"),
  },
  {
    path: "/mobile/payment-failure",
    name: "mobile-payment-failure",
    component: () => import("../views/mobile/PaymentResult.vue"),
  },
  {
    path: "/mobile/saved-cards",
    name: "mobile-saved-cards",
    component: () => import("../views/mobile/SavedCards.vue"),
  },
  {
    path: "/zakat",
    name: "zakat",
    component: ZakatOptions,
  },
  {
    path: "/zakat/donate",
    name: "zakat-donate",
    component: ZakatDonate,
  },
  {
    path: "/zakat/calculate",
    name: "zakat-calculate",
    component: ZakatCalculate,
  },
  {
    path: "/cases-list",
    name: "cases-list",
    component: CasesList,
  },
  {
    path: "/charity/:id/cases-list",
    name: "charity-cases-list",
    component: CharityCasesList,
  },
  {
    path: "/charity-list",
    name: "charity-list",
    component: CharityList,
  },
  {
    path: "/waqf-list",
    name: "waqf-list",
    component: WaqfList,
  },
  {
    path: "/campaign-list",
    name: "campaign-list",
    component: CampaignList,
  },
  {
    path: "/health-initiatives",
    name: "health-initiatives",
    component: () =>
      import("../views/health-initiatives/HealthInitiatives.vue"),
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("../views/privacy-policy/PrivacyPolicy.vue"),
  },
  {
    path: "/completed-cases",
    name: "completed-cases",
    component: () => import("../views/completed-cases/CompletedCases.vue"),
  },
  {
    path: "/ambassador/top",
    name: "ambassador-top",
    component: () => import("../views/ambassador/TopAmbassadors.vue"),
  },
  {
    path: "/major-donors",
    name: "major-donors",
    component: () => import("../views/major-donors/MajorDonors.vue"),
  },
  {
    path: "/major-donors-login",
    name: "major-donors-login",
    meta: { authPage: true },
    component: () => import("../views/major-donors/SignIn.vue"),
  },
  {
    path: "/my-wallets",
    name: "major-donors-my-wallets",
    component: () => import("../views/major-donors/MyWallets.vue"),
  },
  {
    path: "/donation-logs",
    name: "major-donors-donation-logs",
    component: () => import("../views/major-donors/DonationLogs.vue"),
  },
  {
    path: "/faq",
    name: "faq",
    component: () => import("../views/faq/Faq.vue"),
  },
  {
    path: "/all-cases-donation",
    name: "all-cases-donation",
    component: () => import("../views/all-cases-donation/AllCasesDonation.vue"),
  },
  {
    path: "/periodic-donation",
    name: "periodic-donation",
    component: () => import("../views/periodic-donation/PeriodicDonation.vue"),
  },
  {
    path: "/checkout",
    name: "checkout",
    component: Checkout,
  },
  {
    path: "/periodic-donation-checkout",
    name: "periodic-donation-checkout",
    component: PeriodicDonationCheckout,
  },
  {
    path: "/zakat-checkout",
    name: "zakat-checkout",
    component: ZakatCheckout,
  },
  {
    path: "/nearest-charity",
    name: "nearest-charity",
    component: () => import("../views/nearest-charity/LocateCharity.vue"),
  },
  {
    path: '/sehatty',
    name: 'sehatty-home',
    component: HomeView,
  },
  {
    path: '/sehatty/donate-case/:case_id',
    name: 'sehatty-donate-case',
    component: DonateCase,
  },
  {
    path: "/privacy-policy-mobile",
    name: "privacy-policy-mobile",
    component: () => import("../views/privacy-policy/PrivacyPolicy.vue")
  },

  {
    path: "/admin-panel",
    name: "admin-panel",
    meta: { requiresAuth: true },
    component: () => import("../views/admin-panel/AdminPanel.vue"),
    children: [
      {
        path: "/admin-panel/cases",
        name: "admin-cases",
        redirect: "/admin-panel/auditors-list",
      },
      {
        path: "/admin-panel/cases/group",
        name: "admin-group-cases",
        component: () => import("../views/admin-panel/cases/group/List.vue"),
      },
      {
        path: "/admin-panel/cases/edit/:id",
        name: "admin-cases-list",
        alias: "/admin-panel/cases/edit",
        component: () => import("../views/admin-panel/cases/Edit.vue"),
      },
      {
        path: "/admin-panel/cases/view/:id",
        alias: "/admin-panel/cases/view",
        name: "admin-cases-view",
        component: () => import("../views/admin-panel/cases/View.vue"),
      },
      {
        path: "/admin-panel/cases/create/:step",
        alias: "/admin-panel/cases/create",
        name: "admin-cases-create",
        component: () => import("../views/admin-panel/cases/Create.vue"),
      },
      {
        path: "/admin-panel/cases/group/create",
        name: "admin-cases-group-create",
        component: () => import("../views/admin-panel/cases/group/Create.vue"),
      },
      {
        path: "/admin-panel/cases/group/edit/:id",
        name: "admin-cases-group-edit",
        component: () => import("../views/admin-panel/cases/group/Edit.vue"),
      },
      {
        path: "/admin-panel/cases/group/view/:id",
        name: "admin-cases-group-view",
        component: () => import("../views/admin-panel/cases/group/View.vue"),
      },
      {
        path: "/admin-panel/auditors-list",
        name: "auditors-list",
        component: () => import("../views/admin-panel/cases/AuditorsList.vue"),
      },
      {
        path: "/admin-panel/auditor-case-detail/view/:id",
        name: "auditor-case-detail-view",
        component: () =>
          import("../views/admin-panel/cases/AuditorCaseDetailView.vue"),
      },
      {
        path: "/admin-panel/auditor-case-detail/edit/:id",
        name: "auditor-case-detail-edit",
        component: () =>
          import("../views/admin-panel/cases/AuditorCaseDetailEdit.vue"),
      },
      {
        path: "/admin-panel/charity-representative",
        name: "charity-representative-list",
        component: () =>
          import("../views/admin-panel/charity-representative/List.vue"),
      },
      {
        path: "/admin-panel/charity-representative/:id",
        name: "charity-representative-user",
        component: () =>
          import("../views/admin-panel/charity-representative/User.vue"),
      },
      {
        path: "/admin-panel/charity-representative/create/:step",
        name: "charity-representative-create",
        alias: "/admin-panel/charity-representative/create",
        component: () =>
          import("../views/admin-panel/charity-representative/Create.vue"),
      },
      {
        path: "/admin-panel/request",
        name: "admin-requests",
        component: () => import("../views/admin-panel/requests/Requests.vue"),
      },
      {
        path: "/admin-panel/charities",
        name: "admin-charities",
        component: () => import("../views/admin-panel/requests/Requests.vue"),
      },
      {
        path: "/admin-panel/statistics",
        name: "admin-statistics",
        component: () =>
          import("../views/admin-panel/statistics/Statistics.vue"),
      },
      {
        path: "/admin-panel/user-profile",
        name: "user-profile",
        component: () =>
          import("../views/admin-panel/user-profile/DonorProfile.vue"),
      },
      {
        path: "/admin-panel/periodic-donation",
        name: "admin-panel-periodic-donation",
        component: () =>
          import("../views/admin-panel/periodic-donation/PeriodicDonation.vue"),
      },
      {
        path: "/admin-panel/user-profile-charity-manager",
        name: "user-profile-charity-manager",
        component: () =>
          import("../views/admin-panel/user-profile/CharityProfile.vue"),
      },
      {
        path: "/admin-panel/user-profile-charity-representative",
        name: "user-profile-charity-representative",
        component: () =>
          import("../views/admin-panel/user-profile/CharityProfile.vue"),
      },
      {
        path: "/admin-panel/request-details/:id",
        name: "request-details",
        component: () =>
          import("../views/admin-panel/request-details/RequestDetails.vue"),
      },
      {
        path: "/admin-panel/charity-details/:id",
        name: "charity-details",
        component: () =>
          import("../views/admin-panel/request-details/RequestDetails.vue"),
      },
      {
        path: "/admin-panel/charity-manager-update-request/:id",
        name: "charity-manager-update-request",
        component: () =>
          import("../views/admin-panel/request-details/RequestDetails.vue"),
      },
      {
        path: "/admin-panel/change-charity-manager",
        name: "change-charity-manager",
        component: () =>
          import(
            "../views/admin-panel/change-charity-manager/ChangeCharityManager.vue"
          ),
      },
      {
        path: "/admin-panel/wallets/private/create",
        name: "wallet-create",
        component: () =>
          import("../views/admin-panel/wallets/private/Create.vue"),
      },
      {
        path: "/admin-panel/wallets/:id/edit",
        name: "wallet-edit",
        component: () =>
          import("../views/admin-panel/wallets/private/Edit.vue"),
      },
      {
        path: "/admin-panel/wallets/donor",
        name: "donor-wallet-grid",
        component: () => import("../views/admin-panel/wallets/donor/List.vue"),
      },
      {
        path: "/admin-panel/wallets/donor/create",
        name: "donor-wallet-create",
        component: () =>
          import("../views/admin-panel/wallets/donor/Create.vue"),
      },
      {
        path: "/admin-panel/wallets/donor/:id/view",
        name: "donor-wallet-view",
        component: () =>
          import("../views/admin-panel/wallets/donor/Details.vue"),
      },
      {
        path: "/admin-panel/wallets/donor/:id/edit",
        name: "donor-wallet-edit",
        component: () => import("../views/admin-panel/wallets/donor/Edit.vue"),
      },
      {
        path: "/admin-panel/wallet/donor/statistics/:id",
        name: "donor-wallet-statistics",
        component: () =>
          import("../views/admin-panel/wallets/donor/Statistics.vue"),
      },
      {
        path: "/admin-panel/wallets/donor/:id/operations",
        name: "donor-wallet-operations",
        component: () =>
          import("../views/admin-panel/wallets/donor/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/wallets/donor/:id/donate",
        name: "donor-wallets-donate",
        component: () =>
          import("../views/admin-panel/wallets/donor/DonateGrid.vue"),
      },
      {
        path: "/admin-panel/wallets/shefa",
        name: "shefa-wallets",
        component: () => import("../views/admin-panel/wallets/shefa/List.vue"),
      },
      {
        path: "/admin-panel/wallets/shefa/:id/donate",
        name: "shefa-wallets-donate",
        component: () =>
          import("../views/admin-panel/wallets/shefa/DonateGrid.vue"),
      },
      {
        path: "/admin-panel/wallets/shefa/:id/operations",
        name: "shefa-wallets-operations",
        component: () =>
          import("../views/admin-panel/wallets/shefa/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/wallets/transaction",
        name: "wallets-transaction",
        component: () =>
          import("../views/admin-panel/wallets/TopUpTransaction.vue"),
      },
      {
        path: "/admin-panel/wallets/cases",
        name: "cases-wallets",
        component: () => import("../views/admin-panel/wallets/cases/List.vue"),
      },
      {
        path: "/admin-panel/wallets/cases/:id/operations",
        name: "cases-wallets-operations",
        component: () =>
          import("../views/admin-panel/wallets/cases/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/wallets/private",
        name: "private-wallets",
        component: () =>
          import("../views/admin-panel/wallets/private/List.vue"),
      },
      {
        path: "/admin-panel/wallets/private/:id/operations",
        name: "private-wallets-operations",
        component: () =>
          import("../views/admin-panel/wallets/private/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/wallets/private/:id/donate",
        name: "private-wallets-donate",
        component: () =>
          import("../views/admin-panel/wallets/private/DonateGrid.vue"),
      },
      {
        path: "/admin-panel/wallets/:id/view",
        name: "private-wallets-view",
        component: () =>
          import("../views/admin-panel/wallets/private/View.vue"),
      },
      {
        path: "/admin-panel/wallets/advance-donation",
        name: "advance-donation",
        component: () =>
          import("../views/admin-panel/wallets/private/AdvanceDonation.vue"),
      },
      {
        path: "/admin-panel/wallets/awqaf/:id/operations",
        name: "awqaf-wallets-operations",
        component: () =>
          import("../views/admin-panel/wallets/awqaf/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/wallet/reinforcements",
        name: "wallet-reinforcements",
        component: () =>
          import("../views/admin-panel/wallets/reinforcements/List.vue"),
      },
      {
        path: "/admin-panel/wallets/reinforcements/:id/detail/",
        name: "wallet-reinforcement-detail",
        component: () =>
          import("../views/admin-panel/wallets/reinforcements/Detail.vue"),
      },
      {
        path: "/admin-panel/wallets/reinforcements/:id/edit/",
        name: "wallet-reinforcement-edit",
        component: () =>
          import("../views/admin-panel/wallets/reinforcements/Edit.vue"),
      },
      {
        path: "/admin-panel/financial-operations",
        name: "financial-operations",
        component: () =>
          import(
            "../views/admin-panel/financial-operations/FinancialOperationsLogs.vue"
          ),
      },
      {
        path: "/admin-panel/bank-transactions",
        name: "bank-transactions-operations",
        component: () =>
          import(
            "../views/admin-panel/financial-operations/BankTransactionsOperationsLogs.vue"
          ),
      },
      {
        path: "/admin-panel/wallets/campaign/:id/operations",
        name: "campaign-wallets-operations",
        component: () =>
          import("../views/admin-panel/wallets/campaign/OperationsLogs.vue"),
      },
      {
        path: "/admin-panel/waqf",
        name: "waqf",
        component: () => import("../views/admin-panel/waqf/List.vue"),
      },
      {
        path: "/admin-panel/waqf/create",
        name: "waqf-create",
        component: () => import("../views/admin-panel/waqf/Create.vue"),
      },
      // Invoicing PaymentRequest routes
      {
        path: "/admin-panel/payment-request",
        name: "payment-requests-list",
        component: () =>
          import("../views/admin-panel/payment-requests/List.vue"),
      },
      {
        path: "/admin-panel/payment-request/create/:id",
        name: "payment-request-create",
        component: () =>
          import("../views/admin-panel/payment-requests/Create.vue"),
      },
      {
        path: "/admin-panel/payment-request/view/:id",
        name: "payment-request-view",
        component: () =>
          import("../views/admin-panel/payment-requests/View.vue"),
      },
      {
        path: "/admin-panel/payment-request/edit/:id",
        name: "payment-request-edit",
        component: () =>
          import("../views/admin-panel/payment-requests/Edit.vue"),
      },
      {
        path: "/admin-panel/campaigns",
        name: "campaigns",
        component: () => import("../views/admin-panel/campaigns/List.vue"),
      },
      {
        path: "/admin-panel/campaigns/create",
        name: "campaigns-create",
        component: () => import("../views/admin-panel/campaigns/Create.vue"),
      },
      {
        path: "/admin-panel/donations-log",
        name: "donations-log",
        component: () =>
          import("../views/admin-panel/donations-log/DonationsLog.vue"),
      },
      {
        path: "/admin-panel/ambassador/list",
        name: "ambassador-list",
        component: () => import("../views/ambassador/List.vue"),
      },
      {
        path: "/admin-panel/support-tickets",
        name: "support-tickets",
        component: () =>
          import("../views/admin-panel/support-tickets/SupportTickets.vue"),
      },
      {
        path: "/admin-panel/support-tickets/view/:id",
        name: "support-tickets-view",
        component: () =>
          import("../views/admin-panel/support-tickets/Details.vue"),
      },
      {
        path: "/admin-panel/support-tickets/edit/:id",
        name: "support-tickets-edit",
        component: () =>
          import("../views/admin-panel/support-tickets/Details.vue"),
      },
      {
        path: "/admin-panel/treatment-requests",
        name: "treatment-requests",
        component: () =>
          import("../views/admin-panel/treatment-requests/List.vue"),
      },
      {
        path: "/admin-panel/treatment-requests/create",
        name: "treatment-requests-create",
        component: () =>
          import("../views/admin-panel/treatment-requests/Create.vue"),
      },
      {
        path: "/admin-panel/treatment-requests/view/:id",
        name: "treatment-requests-view",
        component: () =>
          import("../views/admin-panel/treatment-requests/Details.vue"),
      },
      {
        path: "/admin-panel/treatment-requests/edit/:id",
        name: "treatment-requests-edit",
        component: () =>
          import("../views/admin-panel/treatment-requests/Details.vue"),
      },
      {
        path: "/admin-panel/treatment-requests/cancelled",
        name: "treatment-requests-cancelled",
        component: () =>
          import("../views/admin-panel/treatment-requests/List.vue"),
      },
      // Hospital intake channel. Same views as the standard treatment
      // requests - the list narrows itself to `is_shefa_charity` from the
      // path, and the create form hides the association selector - so the two
      // channels cannot drift apart in layout or behaviour.
      {
        path: "/admin-panel/hospital-treatment-requests",
        name: "hospital-treatment-requests",
        component: () =>
          import("../views/admin-panel/treatment-requests/List.vue"),
      },
      {
        path: "/admin-panel/hospital-treatment-requests/create",
        name: "hospital-treatment-requests-create",
        // Role not loaded yet (undefined): let the page load, Create.vue
        // re-checks once it arrives.
        beforeEnter: () => {
          const roleKey = (store.state as any).adminPanelModule?.userRoleKey;
          if (roleKey === undefined || canCreateHospitalRequest(roleKey)) return true;
          return { path: "/admin-panel/hospital-treatment-requests" };
        },
        component: () =>
          import("../views/admin-panel/treatment-requests/Create.vue"),
      },
      {
        path: "/admin-panel/hospital-treatment-requests/view/:id",
        name: "hospital-treatment-requests-view",
        component: () =>
          import("../views/admin-panel/treatment-requests/Details.vue"),
      },
      {
        path: "/admin-panel/hospital-treatment-requests/edit/:id",
        name: "hospital-treatment-requests-edit",
        // SHEFA2-4602: the Technical Auditor is read-only on the hospital
        // channel - a typed/bookmarked edit URL sends them to the view
        // route instead, the same way Details.vue itself already redirects
        // a rejected request's edit URL. Role not loaded yet (undefined):
        // let the page load, same as the create guard above.
        beforeEnter: (to) => {
          const roleKey = (store.state as any).adminPanelModule?.userRoleKey;
          if (roleKey === undefined || !isTechnicalAuditorRole(roleKey)) return true;
          return { path: `/admin-panel/hospital-treatment-requests/view/${to.params.id}` };
        },
        component: () =>
          import("../views/admin-panel/treatment-requests/Details.vue"),
      },
      {
        path: "/admin-panel/user-audit-log",
        name: "AuditLogs",
        component: () => import("../views/admin-panel/audit-logs/List.vue"),
      },
      {
        path: "/admin-panel/notifications",
        name: "notifications",
        component: () => import("../views/admin-panel/manage-notifications/ManageNotifications.vue"),
      },
      {
        path: "/admin-panel/notifications/create",
        name: "notifications-create",
        component: () => import("../views/admin-panel/manage-notifications/CreateNotification.vue"),
      },
      {
        path: "/admin-panel/notifications/view/:id",
        name: "notifications-view",
        component: () => import("../views/admin-panel/manage-notifications/ViewNotification.vue"),
      },
      {
        path: "/admin-panel/notifications/edit/:id",
        name: "notifications-edit",
        component: () => import("../views/admin-panel/manage-notifications/EditNotification.vue"),
      },
      {
        path: "/admin-panel/users-report/donors",
        name: "users-report-donors",
        component: () => import("../views/admin-panel/reports/users/donors/List.vue"),
      },
      {
        path: "/admin-panel/users-report/auditors",
        name: "users-report-auditors",
        component: () => import("../views/admin-panel/reports/users/auditors/List.vue"),
      },
      {
        path: "/admin-panel/users-report/charities",
        name: "users-report-charities",
        component: () => import("../views/admin-panel/reports/users/charities/List.vue"),
      },
      {
        path: "/admin-panel/cases-report/active",
        name: "cases-report-active",
        component: () => import("../views/admin-panel/reports/cases/active/List.vue"),
      },
      {
        path: "/admin-panel/cases-report/completed",
        name: "cases-report-completed",
        component: () => import("../views/admin-panel/reports/cases/completed/List.vue"),
      },
      {
        path: "/admin-panel/donations-report/gifts",
        name: "donations-report-gifts",
        component: () => import("../views/admin-panel/reports/donations/gifts/List.vue"),
      },
      {
        path: "/admin-panel/donations-report/recurring",
        name: "donations-report-recurring",
        component: () => import("../views/admin-panel/reports/donations/recurring/List.vue"),
      },
      {
        path: "/admin-panel/donations-report/published-pages",
        name: "donations-report-published-pages",
        component: () => import("../views/admin-panel/reports/published-pages/List.vue"),
      },
      {
        path: "/admin-panel/dashboard",
        name: "dashboard",
        component: () => import("../views/admin-panel/dashboards/Dashboards.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/products",
        name: "waqf-product",
        component: () => import("../views/admin-panel/waqf-products/List.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/create",
        name: "waqf-product-create",
        component: () => import("../views/admin-panel/waqf-products/Create.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/products/:id/view",
        name: "waqf-product-view",
        component: () => import("../views/admin-panel/waqf-products/Details.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/products/:id/edit",
        name: "waqf-product-edit",
        component: () => import("../views/admin-panel/waqf-products/Details.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/transfer-manager/products/:id/view",
        name: "waqf-transfer-manager-product-view",
        component: () => import("../views/admin-panel/waqf-products/Details.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/transfer-manager/products/:id/edit",
        name: "waqf-transfer-manager-product-edit",
        component: () => import("../views/admin-panel/waqf-products/Details.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/categories",
        name: "waqf-category-list",
        component: () => import("../views/admin-panel/waqf-products/categories/List.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/categories/create",
        name: "waqf-category-create",
        component: () => import("../views/admin-panel/waqf-products/categories/Create.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/categories/:id/edit",
        name: "waqf-category-edit",
        component: () => import("../views/admin-panel/waqf-products/categories/Details.vue"),
      },
      {
        path: "/admin-panel/hef-waqf/categories/:id/view",
        name: "waqf-category-view",
        component: () => import("../views/admin-panel/waqf-products/categories/Details.vue"),
      }
    ]
  },
  {
    path: "/support-tickets/create",
    name: "support-tickets-create",
    meta: { requiresAuth: true, redirectAfterAuth: true },
    component: () => import("../views/admin-panel/support-tickets/Create.vue"),
  },
  {
    path: "/under-maintenance",
    name: "maintenance",
    component: () => import("../views/maintenance/maintenance-page.vue")
  },
  {
    path: "/:catchAll(.*)",
    name: "not-found",
    component: () => import("../views/404/404-page.vue")
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savePosition) {
    return {
      top: 0,
      behavior: "smooth",
    };
  },
});

router.beforeEach((to, from) => {
  const isMaintenanceMode = process.env.VUE_APP_MAINTENANCE_MODE === "true";
  const isLoadTest = to.query.loadtest === "true";
  if (isMaintenanceMode && to.name !== "maintenance" && !isLoadTest) {
    return { name: "maintenance" };
  }

  if (
    store.getters.isAuthenticated &&
    !JwtUtilService.getToken(JwtUtilService.access_token_key) &&
    !JwtUtilService.getToken(JwtUtilService.refresh_token_key)
  ) {
    store.dispatch(STORE_ENUM.SIGN_OUT);
  }

  if (store.getters.isAuthenticated) {
    const charityInfo = getLocalValue("charity_approval_info");

    if (charityInfo) {
      const {
        is_password_set,
        is_signed_agreement_uploaded,
        is_agreement_approved,
      } = charityInfo;

      if (!is_password_set && to.name !== "reset-password") {
        return { name: "reset-password" };
      }
      if (
        is_password_set &&
        !is_signed_agreement_uploaded &&
        to.name !== "upload-signed-agreement"
      ) {
        return { name: "upload-signed-agreement" };
      }
      if (
        is_password_set &&
        is_signed_agreement_uploaded &&
        !is_agreement_approved &&
        to.name !== "agreement-approval"
      ) {
        return { name: "agreement-approval" };
      }
    }
  }

  const donorEmail = getLocalValue('useremail') || null;
  const isAuthenticatedDonor = (store.getters.isDonor && store.getters.isAuthenticated) || false;

  if (!donorEmail && isAuthenticatedDonor && to.name !== 'home') {
    return { name: "home" };
  }

  const isMajorDonor = JwtUtilService.getClientID() === "shefa-major-donor-client" ? true : false;
  if (isMajorDonor && to.name !== 'major-donors-my-wallets' && getLocalValue('passwordSet') !== true) {
    return { name: 'major-donors-my-wallets' };
  }

  const sehatty_token = getLocalValue("sehatty_token");
  store.dispatch("updateSehattyPage", to);
  if (store.getters.isSehattyPage) {
    if (!sehatty_token) {
      const token = to.query.token;
      if (token && verifySehattyToken(token)) {
        setLocalValue("sehatty_token", token);
      } else {
        return { name: "not-found" };
      }
    }
  }

  if (to.meta.requiresAuth && !store.getters.isAuthenticated) {
    if (to.meta.redirectAfterAuth) {
      let route = {
        name: "sign-in",
        query: { tab: 0, redirect: to.path.slice(1) },
      };
      return route;
    }
    return { name: "sign-in" };
  } else if (to.meta.authPage && store.getters.isAuthenticated) {
    return { name: "home" };
  }
  return true;
});

router.beforeEach((to, from) => {
  const currentRouteName = to.name;
  if (!currentRouteName) return;
  
  const isMobileRoute = to.path.includes("/mobile") || to.name?.toString().includes("mobile");
  if (isMobileRoute) return;

  if (!to.query.mode) {
    const persistedMode = from.query.mode || 'patients';
    return { ...to, query: { ...to.query, mode: persistedMode } };
  }
});

router.afterEach((to, from) => {
  const { startScroll } = useScrollToSelectedCase();
  const pagesToScroll: any = ["home", "cases-list", "charity-cases-list"];
  if (from.name === "donate-case" && pagesToScroll.includes(to.name)) {
    startScroll();
  } else if (pagesToScroll.includes(to.name)) {
    removeLocalValue("selected_case_id");
    removeLocalValue("selectedPage");
  }
});


router.afterEach((to, from) => {
  const filterPersistenceMap = {
    'auditors-list': ['auditor-case-detail-view', 'auditor-case-detail-edit'],
    'payment-requests-list': ['payment-request-view', 'payment-request-edit', 'payment-request-create'],
  };

  const fromName = from.name;
  const toName = to.name;

  for (const [listRoute, detailRoutes] of Object.entries(filterPersistenceMap)) {
    if (fromName && detailRoutes.includes(fromName.toString()) && toName !== listRoute) {
      removeLocalValue(`${listRoute}-filters`);
    }
  }
});
export default router;
